import React, {Component} from 'react';
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import { RBSVariantEnum } from '../Settings';
import { InputStateControl } from '../utils/InputStateControl';
import { ValidationSchemes } from '../Settings';

export interface RBSDropdownItem {
    label?: string;
    value?: string;
    is_divider?: boolean;
}

export interface RBSDropdownProps {
    /** Label shown above the dropdown (FloatingLabel). */
    label?: string;
    /** List of dropdown options, each with label, value, and optional divider. */
    items: Array<RBSDropdownItem>;
    /** The currently selected value in the dropdown. */
    prefill?: string;
    /** Callback fired when the selection changes. */
    onChange?: (value: string) => void;
    /** Optional HTML id for the dropdown. */
    id?: string;
    /** Style variant for the dropdown (not used in current render). */
    variant?: RBSVariantEnum;
    /** If true, disables the dropdown. */
    disabled?: boolean;
    /** Custom CSS class for styling. */
    className?: string;
    /** Custom inline style for the dropdown. */
    style?: React.CSSProperties;
    /** Optional controller for input state and validation. */
    input_state_control?: InputStateControl;
    /** Unique key for registration with input_state_control. */
    input_key?: string;
    /** Validation rules for the dropdown value. */
    validation_schemes?: Array<ValidationSchemes>;
    /** Optional hover-over text to provide additional user guidance. */
    title?: string;
}

interface RBSDropdownState {
    current_value: string;
    valid: boolean | undefined;
}

/**
 * RBSDropdown is a controlled dropdown component based on react-bootstrap's DropdownButton.
 * It manages its selected value in state and propagates changes via onChange callback.
 * It is designed to be fully compatible with input state control logic.
 */
export class RBSDropdown extends Component<RBSDropdownProps, RBSDropdownState> {
    private component_id: number | undefined = undefined;

    constructor(props: RBSDropdownProps) {
        super(props);
        this.state = {
            current_value: props.prefill || '',
            valid: undefined
        };
    }

    componentDidMount() {
        if (this.props.input_state_control) {
            this.component_id = this.props.input_state_control.registerComponent(
                this.props.input_key,
                this.props.validation_schemes,
                this.validityUpdate
            );
            if (this.component_id) this.props.input_state_control.updateValue(
                this.component_id,
                this.state.current_value
            );
        }
    }

    componentWillUnmount() {
        if (this.props.input_state_control && this.component_id) {
            this.props.input_state_control.deregisterComponent(this.component_id);
        }
        this.component_id = undefined;
    }

    validityUpdate = (valid: boolean) => {
        this.setState({ valid });
    };

    handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = event.target.value;
        this.setState({ current_value: newValue });
        if (this.props.input_state_control && this.component_id) {
            this.props.input_state_control.updateValue(this.component_id, newValue);
        }
        if (this.props.onChange) {
            this.props.onChange(newValue);
        }
    };

    render() {
        const { items, label, id, disabled, className, style, input_key, validation_schemes, title } = this.props;
        const { current_value, valid } = this.state;

        const showValidity = validation_schemes && valid !== undefined;
        const isValid = showValidity ? valid : undefined;
        const isInvalid = showValidity ? !valid : undefined;

        return (
            <FloatingLabel
                controlId={input_key || id || "rbs-dropdown"}
                label={label || "Select"}
                className="mb-3"
            >
                <Form.Select
                    value={current_value}
                    onChange={this.handleChange}
                    isValid={isValid}
                    isInvalid={isInvalid}
                    disabled={disabled}
                    className={className}
                    style={style}
                    title={title}
                >
                    {items.map((item, idx) =>
                        item.is_divider ? (
                            <option key={"divider-" + idx} disabled>──────────</option>
                        ) : (
                            <option key={"DropDownItem_" + item.value} value={item.value}>{item.label}</option>
                        )
                    )}
                </Form.Select>
            </FloatingLabel>
        );
    }
}
